import React from 'react';
import './TermsOfServicePage.css';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="tos-page">
      <div className="tos-content">
        <h1>CompanySieve Privacy Policy</h1>
        <p><strong>Effective Date:</strong> March 5, 2026</p>
        <p><strong>Last Updated:</strong> March 5, 2026</p>

        <hr />

        <h2>1. Introduction</h2>
        <p>
          This Privacy Policy describes how CompanySieve, LLC ("Company," "we," "us," or "our") collects,
          uses, stores, and shares information when you use the CompanySieve service ("Service") at
          companysieve.com.
        </p>
        <p>
          By creating an account or using the Service, you consent to the practices described in this Privacy
          Policy. If you do not agree, do not use the Service.
        </p>

        <hr />

        <h2>2. Information We Collect</h2>

        <h3>2.1 Information You Provide</h3>
        <ul>
          <li><strong>Account information:</strong> Your name, email address, and password (stored in hashed form) when you register for an account.</li>
          <li><strong>Screening inputs:</strong> Company names, country codes, and domain names you submit for compliance screening.</li>
          <li><strong>Communications:</strong> Any emails or messages you send to us at support@companysieve.com.</li>
        </ul>

        <h3>2.2 Information Generated by the Service</h3>
        <ul>
          <li><strong>Screening results:</strong> The complete output of each screening, including Consolidated Screening List (CSL) matches, OpenSanctions results, AI-generated research, match validation analysis, corporate structure data, domain intelligence, risk assessments, and executive briefs.</li>
          <li><strong>PDF reports:</strong> Compliance memoranda generated from screening results.</li>
        </ul>

        <h3>2.3 Information Collected Automatically</h3>
        <ul>
          <li><strong>Usage data:</strong> Number of screenings performed, AI token consumption, and timestamps of screenings.</li>
          <li><strong>Session data:</strong> Session identifiers and CSRF tokens necessary for authentication and security.</li>
          <li><strong>Server logs:</strong> IP addresses, browser type, and access timestamps recorded in standard web server logs.</li>
        </ul>

        <h3>2.4 Information We Do Not Collect</h3>
        <ul>
          <li>We do not use third-party analytics or tracking services (e.g., Google Analytics).</li>
          <li>We do not use advertising cookies or pixels.</li>
          <li>We do not collect payment card information directly (if payment processing is added in the future, it will be handled by a PCI-compliant third-party processor).</li>
        </ul>

        <hr />

        <h2>3. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li><strong>Provide the Service:</strong> Process your screening queries, generate results, and store your screening history.</li>
          <li><strong>Authenticate you:</strong> Manage your account, verify your identity, and maintain session security.</li>
          <li><strong>Generate reports:</strong> Create downloadable PDF compliance memoranda from your screening results.</li>
          <li><strong>Improve the Service:</strong> Monitor usage patterns (in aggregate) to identify bugs, improve performance, and plan capacity.</li>
          <li><strong>Communicate with you:</strong> Respond to support requests and send service-related notices (e.g., Terms of Service updates).</li>
          <li><strong>Enforce our Terms:</strong> Detect and prevent misuse, fraud, or violations of our Terms of Service.</li>
        </ul>
        <p>We do not use your information for advertising, profiling, or automated decision-making unrelated to the Service.</p>

        <hr />

        <h2>4. How We Share Your Information</h2>

        <h3>4.1 Third-Party Service Providers</h3>
        <p>
          To perform compliance screening, we transmit your screening inputs (company names, country codes,
          and domains) to the following third-party services:
        </p>
        <table className="tos-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Data Shared</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>U.S. International Trade Administration</td>
              <td>Company name, country</td>
              <td>Consolidated Screening List (CSL) search</td>
            </tr>
            <tr>
              <td>OpenSanctions</td>
              <td>Company name, country</td>
              <td>International sanctions screening (EU, UK, UN, and others)</td>
            </tr>
            <tr>
              <td>Google (Gemini AI)</td>
              <td>Company name, country, domain</td>
              <td>AI-powered company research, entity resolution, match validation, corporate structure analysis</td>
            </tr>
            <tr>
              <td>OpenAI</td>
              <td>Company name, country, domain</td>
              <td>Fallback AI provider for research and analysis</td>
            </tr>
            <tr>
              <td>IP2WHOIS</td>
              <td>Domain name</td>
              <td>Domain registration and WHOIS intelligence</td>
            </tr>
          </tbody>
        </table>
        <p>
          Each third-party provider processes your data according to its own privacy policy. We transmit only
          the minimum data necessary for each service to function.
        </p>

        <h3>4.2 We Do Not Sell Your Data</h3>
        <p>We do not sell, rent, or trade your personal information or screening data to any third party.</p>

        <h3>4.3 Legal Requirements</h3>
        <p>
          We may disclose your information if required to do so by law, regulation, legal process, or
          governmental request, or if we believe in good faith that disclosure is necessary to protect our
          rights, your safety, or the safety of others.
        </p>

        <h3>4.4 Business Transfers</h3>
        <p>
          If CompanySieve, LLC is acquired, merged, or sells substantially all of its assets, your information
          may be transferred as part of that transaction. We will notify you of any such change via email or a
          prominent notice on the Service.
        </p>

        <hr />

        <h2>5. Data Security</h2>
        <p>We implement the following security measures to protect your information:</p>
        <ul>
          <li><strong>Encryption at rest:</strong> Company names in screening records are encrypted using industry-standard encryption.</li>
          <li><strong>Password hashing:</strong> Passwords are stored using secure one-way hashing algorithms and are never stored in plaintext.</li>
          <li><strong>Encryption in transit:</strong> All communication between your browser and our servers is encrypted via HTTPS/TLS.</li>
          <li><strong>Session security:</strong> Sessions are protected with CSRF tokens and secure cookie attributes.</li>
          <li><strong>Access controls:</strong> Administrative access to production systems is restricted and authenticated.</li>
        </ul>
        <p>
          No method of transmission over the Internet or electronic storage is 100% secure. While we strive to
          protect your information, we cannot guarantee absolute security.
        </p>

        <hr />

        <h2>6. Data Retention</h2>

        <h3>6.1 Screening History</h3>
        <p>
          Screening results are retained in your account until you delete them. You may delete individual
          screening records at any time through the Service interface.
        </p>

        <h3>6.2 Account Data</h3>
        <p>
          Account information (name, email, hashed password) is retained for as long as your account is active.
        </p>

        <h3>6.3 Server Logs</h3>
        <p>
          Server logs containing IP addresses and access timestamps are retained for up to 90 days for
          security and debugging purposes, then automatically purged.
        </p>

        <h3>6.4 After Account Deletion</h3>
        <p>
          When you request account deletion, we will delete your account information and all associated
          screening records within 30 days. Some data may persist in encrypted backups for up to an additional
          30 days before being permanently removed.
        </p>

        <hr />

        <h2>7. Your Rights</h2>

        <h3>7.1 Access and Portability</h3>
        <p>
          You can access your screening history and account information at any time through the Service. You
          may download PDF reports of your screening results.
        </p>

        <h3>7.2 Deletion</h3>
        <p>You may:</p>
        <ul>
          <li>Delete individual screening records through the Service at any time.</li>
          <li>Request complete account deletion by emailing support@companysieve.com. We will process your request within 30 days.</li>
        </ul>

        <h3>7.3 Correction</h3>
        <p>
          You may update your account information (name, email, password) through the Service at any time.
        </p>

        <h3>7.4 Residents of Specific Jurisdictions</h3>
        <p>
          If you are a resident of a jurisdiction with specific data protection laws (e.g., California, the
          European Union), you may have additional rights. Please contact us at support@companysieve.com to
          exercise any applicable rights. We will respond within the timeframe required by applicable law.
        </p>

        <hr />

        <h2>8. Cookies and Similar Technologies</h2>

        <h3>8.1 Essential Cookies</h3>
        <p>We use only essential cookies necessary for the Service to function:</p>
        <table className="tos-table">
          <thead>
            <tr>
              <th>Cookie</th>
              <th>Purpose</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Session cookie</td>
              <td>Maintains your authenticated session</td>
              <td>Expires on browser close or after inactivity timeout</td>
            </tr>
            <tr>
              <td>CSRF token</td>
              <td>Protects against cross-site request forgery attacks</td>
              <td>Session duration</td>
            </tr>
          </tbody>
        </table>

        <h3>8.2 No Tracking Cookies</h3>
        <p>We do not use analytics cookies, advertising cookies, or any third-party tracking technologies.</p>

        <hr />

        <h2>9. Children's Privacy</h2>
        <p>
          The Service is not directed to individuals under the age of 18. We do not knowingly collect personal
          information from children. If we become aware that a child under 18 has provided us with personal
          information, we will take steps to delete that information.
        </p>

        <hr />

        <h2>10. International Data Transfers</h2>
        <p>
          The Service is operated from the United States. If you access the Service from outside the United
          States, your information will be transferred to and processed in the United States, where data
          protection laws may differ from those in your jurisdiction.
        </p>

        <hr />

        <h2>11. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of material changes by email
          or through the Service at least 30 days before the changes take effect. Your continued use of the
          Service after the effective date of an updated Privacy Policy constitutes acceptance.
        </p>
        <p>The "Last Updated" date at the top of this policy indicates when it was most recently revised.</p>

        <hr />

        <h2>12. Contact</h2>
        <p>If you have questions about this Privacy Policy or wish to exercise your data rights, contact us at:</p>
        <p><strong>Email:</strong> support@companysieve.com</p>

        <hr />

        <p className="tos-draft-notice">
          <em>This document is a draft and should be reviewed by a licensed attorney before publication.</em>
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
